import { useState } from 'react';
import { Alert, Box, Snackbar, Stack, Tooltip, Typography } from '@mui/material';

// --- helpers ---------------------------------------------------------------

// Convert hex + opacity to an rgba() string (for RN shadow color + opacity).
function hexToRgba(hex, opacity = 1) {
  if (typeof hex !== 'string') return `rgba(0,0,0,${opacity})`;
  let h = hex.replace('#', '');
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  const num = parseInt(h, 16);
  if (Number.isNaN(num)) return `rgba(0,0,0,${opacity})`;
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

// React Native shadow token -> CSS box-shadow string.
function rnShadowToCss(token) {
  if (!token || typeof token !== 'object') return 'none';
  const offset = token.shadowOffset || { width: 0, height: 2 };
  const x = offset.width ?? 0;
  const y = offset.height ?? 2;
  const blur = token.shadowRadius ?? 8;
  const color = hexToRgba(token.shadowColor || '#000000', token.shadowOpacity ?? 0.1);
  return `${x}px ${y}px ${blur}px ${color}`;
}

const FONT_WEIGHTS = { regular: 400, medium: 500, semibold: 600, bold: 700 };

function toPx(value) {
  if (typeof value === 'number') return `${value}px`;
  return String(value);
}

// --- viewers ---------------------------------------------------------------

function ShadowViewer({ shadows, onCopy }) {
  return (
    <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 3 }}>
      {shadows.map(({ name, token }) => {
        const css = rnShadowToCss(token);
        return (
          <Box key={name} sx={{ textAlign: 'center' }}>
            <Tooltip title={`box-shadow: ${css} — click to copy`}>
              <Box
                role="button"
                tabIndex={0}
                onClick={() => onCopy(name, css)}
                sx={{
                  width: 160,
                  height: 96,
                  borderRadius: 2,
                  bgcolor: '#fff',
                  boxShadow: css,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  mb: 1,
                }}>
                <Typography variant="caption" color="text.secondary">
                  {name}
                </Typography>
              </Box>
            </Tooltip>
            <Box component="code" sx={{ fontSize: 10, color: 'text.secondary' }}>
              {css}
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
}

function RadiiViewer({ values, onCopy }) {
  return (
    <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 3 }}>
      {Object.entries(values).map(([name, value]) => (
        <Box key={name} sx={{ textAlign: 'center' }}>
          <Tooltip title={`${value} — click to copy`}>
            <Box
              role="button"
              tabIndex={0}
              onClick={() => onCopy(name, toPx(value))}
              sx={{
                width: 88,
                height: 88,
                bgcolor: 'primary.light',
                border: '2px solid',
                borderColor: 'primary.main',
                borderRadius: toPx(value),
                cursor: 'pointer',
                mb: 1,
              }}
            />
          </Tooltip>
          <Typography variant="caption" sx={{ display: 'block' }}>
            {name}
          </Typography>
          <Box component="code" sx={{ fontSize: 11, color: 'text.secondary' }}>
            {toPx(value)}
          </Box>
        </Box>
      ))}
    </Stack>
  );
}

function SpacingViewer({ values, onCopy }) {
  return (
    <Stack spacing={1}>
      {Object.entries(values).map(([name, value]) => (
        <Box
          key={name}
          role="button"
          tabIndex={0}
          onClick={() => onCopy(name, toPx(value))}
          sx={{ display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer' }}>
          <Box component="code" sx={{ minWidth: 56, fontSize: 12 }}>
            {name}
          </Box>
          <Box sx={{ height: 16, width: toPx(value), bgcolor: 'primary.main', borderRadius: 0.5 }} />
          <Box component="code" sx={{ fontSize: 11, color: 'text.secondary' }}>
            {toPx(value)}
          </Box>
        </Box>
      ))}
    </Stack>
  );
}

function FontViewer({ weights, stacks, onCopy }) {
  // Build rows from the weight map (regular/medium/semibold/bold). Apply the real CSS
  // font-weight so samples differ even when the Inter font files aren't loaded on web.
  const entries = Object.keys(weights || {}).length
    ? Object.entries(weights)
    : Object.keys(stacks || {}).map(k => [k, k]);
  const stackFor = key => {
    const stack = (stacks || {})[key];
    if (Array.isArray(stack)) return stack.join(', ');
    if (typeof stack === 'string') return stack;
    return 'Inter, system-ui, sans-serif';
  };
  return (
    <Stack spacing={1.5}>
      {entries.map(([key, family]) => {
        const weight = FONT_WEIGHTS[key] || 400;
        const fontFamily = stackFor(key) || 'Inter, system-ui, sans-serif';
        return (
          <Box
            key={key}
            role="button"
            tabIndex={0}
            onClick={() => onCopy(key, String(family))}
            sx={{
              cursor: 'pointer',
              borderBottom: '1px solid',
              borderColor: 'divider',
              pb: 1,
            }}>
            <Stack direction="row" alignItems="baseline" spacing={2} sx={{ flexWrap: 'wrap' }}>
              <Box component="code" sx={{ minWidth: 80, fontSize: 12 }}>
                {key}
              </Box>
              <Typography sx={{ fontFamily, fontWeight: weight, fontSize: 22 }}>
                The quick brown fox
              </Typography>
            </Stack>
            <Box component="code" sx={{ fontSize: 10, color: 'text.secondary' }}>
              weight {weight} · {fontFamily}
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
}

// --- main ------------------------------------------------------------------

const USAGE_KEY = {
  shadows: 'internalLibraries.tokens_usage_shadows',
  radii: 'internalLibraries.tokens_usage_radii',
  spacing: 'internalLibraries.tokens_usage_spacing',
  fonts: 'internalLibraries.tokens_usage_fonts',
};

// Example-driven viewer for design tokens (shadows / radii / spacing / fonts). Instead of
// dumping raw parameters, it shows the token APPLIED to sample UI so the user sees the
// result and can copy the value. `kind` selects the viewer; `data` carries the resolved
// token values (producer feedback F7).
function TokenGallery({ kind, data, t }) {
  const [copied, setCopied] = useState('');
  const onCopy = (name, value) => {
    if (navigator.clipboard) navigator.clipboard.writeText(value);
    setCopied(name);
  };

  let body = null;
  if (kind === 'shadows') body = <ShadowViewer shadows={data} onCopy={onCopy} />;
  else if (kind === 'radii') body = <RadiiViewer values={data} onCopy={onCopy} />;
  else if (kind === 'spacing') body = <SpacingViewer values={data} onCopy={onCopy} />;
  else if (kind === 'fonts') body = <FontViewer weights={data.weights} stacks={data.stacks} onCopy={onCopy} />;

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        {t(USAGE_KEY[kind] || 'internalLibraries.tokens_usage_generic', 'Use these tokens to keep visual consistency across apps.')}
      </Alert>
      {body}
      <Snackbar
        open={Boolean(copied)}
        autoHideDuration={1500}
        onClose={() => setCopied('')}
        message={t('internalLibraries.colors_copied', { defaultValue: 'Copied {{name}}', name: copied })}
      />
    </Box>
  );
}

export default TokenGallery;
